import { useEffect, useRef, useState } from 'react';
import 'react-calendar/dist/Calendar.css';
import { useNavigate } from 'react-router-dom';
import { Green3, PrimaryTextColor } from '../../../../app/theme/tokens/colors';
import { PrimaryButton } from '../../../../shared/ui/Inputs';
import { Spinner } from '../../../../shared/ui/Spinners';
import { WideLayout } from '../../../../shared/layout/widelayout';
import { FinishOrderLayout } from './FinishOrderLayout';
import { SimplePopup } from '../../../../shared/overlays';
import { DeliveryConfiguration } from './deliveryConfiguration';
import { BiErrorCircle } from 'react-icons/bi';
import { ErrorRed } from '../../../../app/theme/tokens/colors';
import { formatCPF } from '../../../../utils/format';
import { usePortalStore } from '../../../../stores/portalStore';
import {
  displayOrDash,
  formatDocumentOrDash,
  getPatientByCPF,
  getPatientBySap,
} from '../../../../services/patients/patientsService';
import { useCompaniesCatalog } from '../../../../hooks/useCompaniesCatalog';

export const DefaultFinishOrder = ({
  selectedPatient,
  topTileRender,
  bottomTileRender,
  summaryRender,
  addressRender,
  orderType,
  onFinish,
  fastDeliveryLabel,
  scheduleDeliveryLabel,
  hasFastDelivery,
  loadPatientDetails = true,
  patientDetailsLookup = 'cpf',
}) => {
  const currentUser = usePortalStore((state) => state.currentUser);
  const { resolveCompanyName } = useCompaniesCatalog();
  const [patientDetails, setPatientDetails] = useState(null);
  const normalizeRawErrorMessage = (err) => {
    if (err == null) return '';
    if (typeof err === 'string') return err;
    if (typeof err?.message === 'string') return err.message;
    try {
      return JSON.stringify(err);
    } catch {
      return String(err);
    }
  };

  const extractMessageFromJsonString = (raw) => {
    if (typeof raw !== 'string') return null;
    const s = raw.trim();
    if (!s.startsWith('{') || !s.endsWith('}')) return null;
    try {
      const parsed = JSON.parse(s);
      if (parsed && typeof parsed === 'object') {
        if (typeof parsed.message === 'string' && parsed.message.trim())
          return parsed.message;
        if (typeof parsed.sapMessage === 'string' && parsed.sapMessage.trim())
          return parsed.sapMessage;
      }
    } catch {
      return null;
    }
    return null;
  };

  const mapOrderCreationErrorMessage = (rawMessage) => {
    const raw = String(rawMessage ?? '').trim();
    const msgFromJson = extractMessageFromJsonString(raw);
    const msg = String(msgFromJson ?? raw).trim();
    const upper = msg.toUpperCase();

    if (!msg) return 'Erro ao criar pedido. Tente novamente.';

    // Validation / missing fields
    if (
      upper.includes('[CREATE][VALIDATION]') ||
      upper.includes('REQUEST_NUMBER_TEMP') ||
      upper.includes('FIELD IS MANDATORY')
    ) {
      return 'Não foi possível criar o pedido: existem campos obrigatórios ausentes ou inválidos.';
    }

    // SAP customer missing / customer data
    if (
      upper.includes('SAP_CUSTOMER AUSENTE') ||
      upper.includes('SAP CUSTOMER AUSENTE')
    ) {
      return 'Não foi possível criar o pedido: cliente SAP não encontrado para o paciente.';
    }

    // SAP integration failures
    if (
      upper.includes('[SAP SERVICE]') ||
      upper.includes('[CREATE][SAP]') ||
      (upper.includes('INTEGRA') && upper.includes('SAP'))
    ) {
      if (
        upper.includes('[HTTP 401]') ||
        upper.includes('401') ||
        upper.includes('UNAUTHORIZED')
      ) {
        return 'Sessão expirada ou não autorizada para integrar com o SAP. Faça login novamente.';
      }
      return 'Pedido não pôde ser enviado ao SAP no momento. Tente novamente em alguns minutos.';
    }

    // Network / connectivity
    if (
      upper.includes('ETIMEDOUT') ||
      upper.includes('ECONNREFUSED') ||
      upper.includes('NETWORK') ||
      upper.includes('TIMEOUT')
    ) {
      return 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.';
    }

    // Fallback
    return 'Erro ao criar pedido. Tente novamente.';
  };

  const [showSavingPopup, setShowSavingPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [newOrderNumber, setNewOrderNumber] = useState(null);
  const [errorPopupMessage, setErrorPopupMessage] = useState('');
  const navigate = useNavigate();
  const submittingRef = useRef(false);

  const hasValue = (value) =>
    value !== undefined && value !== null && String(value).trim() !== '';

  const valueOrDash = (value) => {
    if (!hasValue(value)) return '-';
    if (typeof value === 'object') {
      return valueOrDash(value?.name ?? value?.label ?? value?.description);
    }
    return displayOrDash(String(value).trim(), {
      zeroAsDash: true,
      repeatedDigitsAsDash: true,
    });
  };

  const firstFilled = (...values) => {
    for (const value of values) {
      if (hasValue(value)) return value;
    }
    return '';
  };

  const getSapCode = () =>
    firstFilled(
      selectedPatient?.sap,
      selectedPatient?.sap_client,
      selectedPatient?.sap_customer,
      selectedPatient?.sap_number,
      selectedPatient?.receiving_code,
      selectedPatient?.id,
      selectedPatient?.ballast?.sap_customer,
      selectedPatient?.ballast?.receiving_code
    );

  const formatCNPJ = (cnpj) => {
    const digits = String(cnpj ?? '').replace(/\D/g, '');
    if (digits.length !== 14 || /^(\d)\1+$/.test(digits)) return '-';
    return digits.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      '$1.$2.$3/$4-$5'
    );
  };

  const formatCEP = (cep) => {
    const digits = String(cep ?? '').replace(/\D/g, '');
    if (digits.length !== 8 || /^(\d)\1+$/.test(digits))
      return valueOrDash(cep);
    return digits.replace(/^(\d{5})(\d{3})$/, '$1-$2');
  };

  const getCompanyLabel = () =>
    resolveCompanyName(
      selectedPatient?.cnpj,
      selectedPatient?.company ?? selectedPatient?.cnpj ?? ''
    );

  const SummaryField = ({ label, value }) => (
    <div
      style={{
        flex: '0 1 auto',
        minWidth: '180px',
        color: PrimaryTextColor,
        lineHeight: 1.35,
      }}
    >
      <strong style={{ color: '#000', marginRight: '4px' }}>{label}:</strong>
      <span style={{ wordBreak: 'break-word' }}>{valueOrDash(value)}</span>
    </div>
  );

  const defaultSummaryRender = () => (
    <div
      style={{
        margin: '0px 15px 10px',
        padding: '12px 15px',
        background: '#f7f7f7',
        borderRadius: '8px',
        boxSizing: 'border-box',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px 24px',
        alignItems: 'center',
        fontSize: '14px',
      }}
    >
      <SummaryField label="Paciente" value={selectedPatient?.name} />
      <SummaryField
        label="CPF"
        value={formatDocumentOrDash(selectedPatient?.cpf, formatCPF, {
          documentType: 'cpf',
        })}
      />
      <SummaryField label="Empresa" value={getCompanyLabel()} />
      <SummaryField label="CNPJ" value={formatCNPJ(selectedPatient?.cnpj)} />
      <SummaryField
        label="Solicitante"
        value={currentUser?.name ?? currentUser?.email}
      />
    </div>
  );

  const defaultAddressRender = () => {
    const ballast = selectedPatient?.ballast ?? {};
    const addressSource = patientDetails ?? {};
    const address = {
      city: firstFilled(
        addressSource.city,
        selectedPatient?.city,
        ballast.delivery_city
      ),
      address: firstFilled(
        addressSource.address,
        selectedPatient?.address,
        ballast.delivery_address
      ),
      addressLine2: firstFilled(
        addressSource.addressLine2,
        selectedPatient?.addressLine2,
        selectedPatient?.delivery_complement,
        ballast.delivery_complement
      ),
      cep: firstFilled(
        addressSource.cep,
        selectedPatient?.cep,
        ballast.delivery_zipcode
      ),
      number: firstFilled(
        addressSource.number,
        selectedPatient?.number,
        selectedPatient?.addressNumber,
        selectedPatient?.delivery_number,
        ballast.delivery_number
      ),
      neighborhood: firstFilled(
        addressSource.neighborhood,
        selectedPatient?.neighborhood,
        selectedPatient?.delivery_neighborhood,
        ballast.delivery_neighborhood
      ),
      uf: firstFilled(
        addressSource.uf,
        selectedPatient?.uf,
        selectedPatient?.delivery_uf,
        ballast.delivery_uf
      ),
    };
    const addressRows = [
      { label: 'Cidade', value: address.city },
      { label: 'Endereço', value: address.address },
      { label: 'Número', value: address.number },
      { label: 'CEP', value: formatCEP(address.cep) },
      { label: 'Complemento', value: address.addressLine2 },
      { label: 'Bairro', value: address.neighborhood },
      { label: 'UF', value: address.uf },
    ];

    return (
      <ul
        style={{
          listStyle: 'none',
          marginTop: '0px',
          marginBottom: '10px',
          padding: '10px',
          background: 'rgb(247, 247, 247)',
          borderRadius: '8px',
        }}
      >
        {addressRows.map((field) => (
          <li
            key={field.label}
            style={{ margin: '0px 0px 6px', padding: '0px' }}
          >
            <label
              style={{
                fontWeight: '300',
                fontSize: '14px',
                wordBreak: 'break-word',
              }}
            >
              <strong style={{ fontWeight: 700 }}>{field.label}:</strong>{' '}
              {valueOrDash(field.value)}
            </label>
          </li>
        ))}
      </ul>
    );
  };

  useEffect(() => {
    (async () => {
      const sap = getSapCode();
      const shouldSearchBySap = patientDetailsLookup === 'sap' && hasValue(sap);
      const shouldSearchByCpf = hasValue(selectedPatient?.cpf);

      if (!loadPatientDetails || (!shouldSearchBySap && !shouldSearchByCpf)) {
        setPatientDetails(null);
        return;
      }

      try {
        const patient = shouldSearchBySap
          ? await getPatientBySap(selectedPatient?.cnpj, sap)
          : await getPatientByCPF(selectedPatient?.cnpj, selectedPatient.cpf);
        setPatientDetails(patient ?? null);
      } catch (error) {
        console.warn(
          '[DefaultFinishOrder] Erro ao buscar dados completos do paciente:',
          error
        );
        setPatientDetails(null);
      }
    })();
  }, [loadPatientDetails, patientDetailsLookup, selectedPatient]);

  const SavingPopup = () => {
    const loading = () => {
      return (
        <div style={{ textAlign: 'center', position: 'relative' }}>
          <Spinner style={{}} />
        </div>
      );
    };

    const saveStatus = () => {
      const rawList = Array.isArray(newOrderNumber)
        ? newOrderNumber.map((x) => String(x))
        : [];
      const uniqueList = Array.isArray(newOrderNumber)
        ? Array.from(new Set(rawList))
        : [];

      let titlePrefix = 'O número do seu pedido é ';
      if (Array.isArray(newOrderNumber) && newOrderNumber.length > 1) {
        titlePrefix = 'Os números dos pedidos gerados são: ';
      }

      const titleValue = Array.isArray(newOrderNumber)
        ? uniqueList.join(',')
        : newOrderNumber;

      return (
        <div style={{ textAlign: 'center', position: 'relative' }}>
          <h3>
            {titlePrefix}
            <span style={{ color: Green3 }}>{titleValue}</span>
          </h3>
          <div>
            <PrimaryButton onClick={() => navigate('/orders')}>
              OK
            </PrimaryButton>
          </div>
        </div>
      );
    };

    return (
      <SimplePopup
        width={500}
        zIndex={5}
        outlet={<div>{savingOrder ? loading() : saveStatus()}</div>}
      ></SimplePopup>
    );
  };

  const ErrorPopup = () => {
    return (
      <SimplePopup
        zIndex={10000}
        width={600}
        outlet={
          <div>
            <div className="error-message">
              <h3 className="erro-text">
                <BiErrorCircle
                  style={{
                    color: ErrorRed,
                    fontWeight: '500',
                    fontSize: '32px',
                  }}
                />
                Atenção!
              </h3>
            </div>
            <div>
              <p style={{ textAlign: 'center' }}>
                {errorPopupMessage ||
                  'Erro interno contacte o administrador do sistema'}
              </p>
            </div>
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <PrimaryButton
                onClick={() => {
                  setShowErrorPopup(false);
                  setErrorPopupMessage('');
                }}
              >
                Ok
              </PrimaryButton>
            </div>
          </div>
        }
      ></SimplePopup>
    );
  };

  const finishOrder = async (deliveryDate, deliveryTurn) => {
    if (submittingRef.current) return;
    submittingRef.current = true;

    setSavingOrder(true);
    setShowErrorPopup(false);
    setErrorPopupMessage('');
    try {
      const orderNumber = await onFinish(deliveryDate, deliveryTurn);
      if (orderNumber === 'erro interno') {
        setErrorPopupMessage(
          'Erro ao criar pedido. Tente novamente em alguns minutos.'
        );
        setShowErrorPopup(true);
        return;
      }

      if (orderNumber === null) {
        setShowSavingPopup(false);
        return;
      }

      setNewOrderNumber(orderNumber);
      setShowSavingPopup(true);
    } catch (err) {
      const raw = normalizeRawErrorMessage(err);
      const friendly = mapOrderCreationErrorMessage(raw);
      if (raw && friendly && raw !== friendly) {
        console.warn('[DefaultFinishOrder] Order creation error (raw):', raw);
      }

      setErrorPopupMessage(friendly);
      setShowErrorPopup(true);
    } finally {
      setSavingOrder(false);
      submittingRef.current = false;
    }
  };

  return (
    <WideLayout
      outlet={
        <>
          {showSavingPopup ? SavingPopup() : null}
          {showErrorPopup ? ErrorPopup() : null}
          <FinishOrderLayout
            summary={
              typeof summaryRender === 'function'
                ? summaryRender()
                : defaultSummaryRender()
            }
            delivery={
              <DeliveryConfiguration
                selectedPatient={selectedPatient}
                orderType={orderType}
                onFinish={finishOrder}
                savingOrder={savingOrder}
                fastDeliveryLabel={fastDeliveryLabel}
                scheduleDeliveryLabel={scheduleDeliveryLabel}
                _hasFastDelivery={hasFastDelivery}
                addressRender={
                  typeof addressRender === 'function'
                    ? addressRender
                    : defaultAddressRender
                }
              />
            }
            equipment={topTileRender()}
            notes={bottomTileRender?.()}
          />
        </>
      }
    ></WideLayout>
  );
};
